import { authState } from "@/plugins/keycloak";
import { logger } from "../services/LoggerService";
import { useErrorStore } from "@/stores/errorStore";
import router from "@/router";
/**
 * Keycloak Authentication Service Implementation
 * Implements IAuthService interface, acting as an adapter for Keycloak
 * Provides enterprise-grade SSO authentication functionality
 */
export class KeycloakAuthService {
    /**
     * Get current login attempt count
     */
    getLoginAttempts() {
        const attempts = sessionStorage.getItem(KeycloakAuthService.LOGIN_ATTEMPT_STORAGE_KEY);
        const timestamp = sessionStorage.getItem(KeycloakAuthService.LOGIN_ATTEMPT_TIMESTAMP_KEY);
        if (!attempts || !timestamp) {
            return 0;
        }
        const lastAttemptTime = parseInt(timestamp);
        const currentTime = Date.now();
        // Reset attempts if enough time has passed (1 minute)
        if (currentTime - lastAttemptTime > KeycloakAuthService.ATTEMPT_RESET_DURATION) {
            this.resetLoginAttempts();
            return 0;
        }
        return parseInt(attempts) || 0;
    }
    /**
     * Increment login attempt count
     */
    incrementLoginAttempts() {
        logger.debug("incrementLoginAttempts called");
        const currentAttempts = this.getLoginAttempts();
        const newAttempts = currentAttempts + 1;
        logger.debug(`Incrementing from ${currentAttempts} to ${newAttempts}`);
        sessionStorage.setItem(KeycloakAuthService.LOGIN_ATTEMPT_STORAGE_KEY, newAttempts.toString());
        sessionStorage.setItem(KeycloakAuthService.LOGIN_ATTEMPT_TIMESTAMP_KEY, Date.now().toString());
        logger.debug(`Set sessionStorage: ${KeycloakAuthService.LOGIN_ATTEMPT_STORAGE_KEY}=${newAttempts}, ${KeycloakAuthService.LOGIN_ATTEMPT_TIMESTAMP_KEY}=${Date.now()}`);
        logger.debug("Login attempts incremented");
    }
    /**
     * Reset login attempt count
     */
    resetLoginAttempts() {
        sessionStorage.removeItem(KeycloakAuthService.LOGIN_ATTEMPT_STORAGE_KEY);
        sessionStorage.removeItem(KeycloakAuthService.LOGIN_ATTEMPT_TIMESTAMP_KEY);
        logger.debug("Login attempts reset");
    }
    /**
     * Check if maximum login attempts exceeded
     */
    isMaxAttemptsExceeded() {
        const currentAttempts = this.getLoginAttempts();
        return currentAttempts >= KeycloakAuthService.MAX_LOGIN_ATTEMPTS;
    }
    /**
     * Handle max attempts error - log to error store and navigate to error page
     */
    handleMaxAttemptsError(error, errorCode) {
        try {
            const errorStore = useErrorStore();
            errorStore.error({
                message: error.message,
                response: {
                    status: 429, // Too Many Requests
                    data: {
                        success: false,
                        errorCode,
                        title: error.message.includes("reinitialize")
                            ? "Too many reinitialize attempts"
                            : "Too many login attempts",
                    },
                },
            });
            // Navigate to error page
            router.push("/error");
        }
        catch (storeError) {
            // If error store is not available (e.g., in tests), just rethrow original error
            logger.debug("Error store not available:", storeError);
            throw error;
        }
    }
    /**
     * Handle general login/reinitialize errors - log to error store
     */
    handleLoginError(error, errorCode = "LOGIN_FAILED") {
        try {
            const errorStore = useErrorStore();
            const message = errorCode === "REINITIALIZE_FAILED"
                ? "Reinitialization failed. Please try again."
                : "Login failed. Please try again.";
            errorStore.error({
                message,
                response: {
                    status: 401,
                    data: {
                        success: false,
                        errorCode,
                        title: errorCode === "REINITIALIZE_FAILED" ? "Reinitialization Failed" : "Login Failed",
                    },
                },
            });
        }
        catch (storeError) {
            // If error store is not available (e.g., in tests), just log
            logger.debug("Error store not available:", storeError);
        }
    }
    /**
     * Initialize the authentication system
     * Waits for Keycloak initialization to complete
     */
    async initialize() {
        try {
            // Keycloak initialization logic is handled in the plugin
            // Here we just wait for initialization to complete
            return new Promise((resolve) => {
                const checkInitialization = () => {
                    if (authState.isInitialized) {
                        resolve(authState.isAuthenticated);
                    }
                    else {
                        setTimeout(checkInitialization, 100);
                    }
                };
                checkInitialization();
            });
        }
        catch (error) {
            logger.debug("Keycloak initialization failed:", error);
            return false;
        }
    }
    /**
     * Check if user is currently authenticated
     */
    isAuthenticated() {
        return authState.isAuthenticated;
    }
    /**
     * Check if authentication system is initialized
     */
    isInitialized() {
        return authState.isInitialized;
    }
    /**
     * Initiate user login flow
     */
    async login(options) {
        logger.debug("Login method called");
        try {
            // Check if maximum attempts exceeded BEFORE incrementing
            if (this.isMaxAttemptsExceeded()) {
                const attempts = this.getLoginAttempts();
                logger.debug(`Maximum login attempts (${KeycloakAuthService.MAX_LOGIN_ATTEMPTS}) exceeded. Current attempts: ${attempts}`);
                throw new Error(`Too many login attempts. Please wait ${KeycloakAuthService.ATTEMPT_RESET_DURATION / 60000} minutes before trying again.`);
            }
            logger.debug("About to increment login attempts");
            // Use the authState.login method which has proper initialization logic
            // Build login options with redirectUri, loginHint, and organization if provided
            const loginOptions = {};
            if (options?.redirectUri) {
                loginOptions.redirectUri = options.redirectUri;
            }
            if (options?.loginHint) {
                loginOptions.loginHint = options.loginHint;
                logger.debug(`Login with loginHint (email): ${options.loginHint}`);
            }
            if (options?.organization) {
                loginOptions.organization = options.organization;
                logger.debug(`Login with organization (from redirect context): ${options.organization}`);
            }
            // Increment attempt counter ONLY when actually attempting login
            this.incrementLoginAttempts();
            logger.debug("Login attempts incremented, about to call authState.login");
            await authState.login(loginOptions);
            logger.debug("authState.login succeeded, resetting attempts");
            // Reset attempts on successful login
            this.resetLoginAttempts();
        }
        catch (error) {
            logger.debug("Keycloak login failed:", error);
            // Handle max attempts error
            if (error instanceof Error && error.message.includes("Too many login attempts")) {
                this.handleMaxAttemptsError(error, "MAX_LOGIN_ATTEMPTS");
                return;
            }
            // Handle other login errors
            this.handleLoginError(error);
            throw error;
        }
    }
    /**
     * Initiate user logout flow
     */
    async logout() {
        try {
            await authState.logout();
        }
        catch (error) {
            logger.debug("Keycloak logout failed:", error);
            throw new Error("Logout failed");
        }
    }
    /**
     * Get current user information from token
     */
    getUserInfo() {
        if (!authState.keycloakInstance?.tokenParsed) {
            return null;
        }
        const tokenParsed = authState.keycloakInstance.tokenParsed;
        return {
            sub: tokenParsed.sub,
            email: tokenParsed.email,
            name: tokenParsed.name,
            preferred_username: tokenParsed.preferred_username,
            roles: tokenParsed.realm_access?.roles || [],
            // Additional user profile fields
            given_name: tokenParsed.given_name,
            family_name: tokenParsed.family_name,
            email_verified: tokenParsed.email_verified,
            locale: tokenParsed.locale || "en", // Extract locale from Keycloak token
        };
    }
    /**
     * Get current access token
     */
    getToken() {
        return authState.keycloakInstance?.token;
    }
    /**
     * Check if token is expired or expiring soon
     */
    isTokenExpired(minValidity = 30) {
        // Use original keycloak instance to avoid reactive proxy issues
        return authState.keycloakInstance?.isTokenExpired(minValidity) ?? true;
    }
    /**
     * Refresh the access token
     */
    async refreshToken() {
        if (!authState.keycloakInstance) {
            return false;
        }
        try {
            const refreshed = await authState.keycloakInstance.updateToken(30);
            return refreshed || authState.keycloakInstance.authenticated || false;
        }
        catch (error) {
            logger.debug("Token refresh failed:", error);
            return false;
        }
    }
    /**
     * Force refresh the access token even if it's still valid
     * This is useful when user data (like locale) has changed in Keycloak
     * and we need to get a fresh token with updated claims
     */
    async forceRefreshToken() {
        if (!authState.keycloakInstance) {
            return false;
        }
        try {
            // Force token refresh by setting a very high minValidity
            // This makes Keycloak think the token is expired and forces a refresh
            const refreshed = await authState.keycloakInstance.updateToken(-1);
            logger.debug("Token force refreshed successfully", {
                refreshed,
                authenticated: authState.keycloakInstance.authenticated,
            });
            return refreshed || authState.keycloakInstance.authenticated || false;
        }
        catch (error) {
            logger.debug("Force token refresh failed:", error);
            return false;
        }
    }
    /**
     * Reinitialize the authentication system
     */
    async reinitialize() {
        try {
            // Check if maximum attempts exceeded BEFORE incrementing
            if (this.isMaxAttemptsExceeded()) {
                const attempts = this.getLoginAttempts();
                logger.debug(`Maximum reinitialize attempts (${KeycloakAuthService.MAX_LOGIN_ATTEMPTS}) exceeded. Current attempts: ${attempts}`);
                throw new Error(`Too many reinitialize attempts. Please wait ${KeycloakAuthService.ATTEMPT_RESET_DURATION / 60000} minutes before trying again.`);
            }
            // Increment attempt counter ONLY when actually attempting reinitialize
            this.incrementLoginAttempts();
            await authState.reinitialize();
            // Reset attempts on successful reinitialize
            this.resetLoginAttempts();
        }
        catch (error) {
            logger.debug("Keycloak reinitialization failed:", error);
            // Handle max attempts error
            if (error instanceof Error && error.message.includes("Too many reinitialize attempts")) {
                this.handleMaxAttemptsError(error, "MAX_REINITIALIZE_ATTEMPTS");
                return;
            }
            // Handle other reinitialize errors
            this.handleLoginError(error, "REINITIALIZE_FAILED");
            throw error;
        }
    }
    /**
     * Get user roles from token (realm_access)
     */
    getUserRoles() {
        const userInfo = this.getUserInfo();
        return userInfo?.roles || [];
    }
    /**
     * Get user roles for a specific client (resource_access)
     * @param clientId - The client ID to get roles for (e.g., 'one-platform-payment')
     */
    getClientRoles(clientId) {
        if (!authState.keycloakInstance?.tokenParsed) {
            return [];
        }
        const tokenParsed = authState.keycloakInstance.tokenParsed;
        return tokenParsed.resource_access?.[clientId]?.roles || [];
    }
    /**
     * Check if user has a specific role
     */
    hasRole(role) {
        const roles = this.getUserRoles();
        return roles.includes(role);
    }
    /**
     * Check if user has any of the specified roles
     */
    hasAnyRole(roles) {
        const userRoles = this.getUserRoles();
        return roles.some((role) => userRoles.includes(role));
    }
    /**
     * Get token remaining validity time in seconds
     */
    getTokenTimeLeft() {
        if (!authState.keycloakInstance?.tokenParsed?.exp) {
            return 0;
        }
        const now = Math.ceil(Date.now() / 1000);
        return authState.keycloakInstance.tokenParsed.exp - now;
    }
    /**
     * Check if token should be refreshed based on remaining time
     */
    shouldRefreshToken(minValidity = 300) {
        const timeLeft = this.getTokenTimeLeft();
        return timeLeft < minValidity && timeLeft > 0;
    }
    /**
     * Get remaining time until login attempts reset (in minutes)
     */
    getLoginAttemptsResetTime() {
        const timestamp = sessionStorage.getItem(KeycloakAuthService.LOGIN_ATTEMPT_TIMESTAMP_KEY);
        if (!timestamp) {
            return 0;
        }
        const lastAttemptTime = parseInt(timestamp);
        const currentTime = Date.now();
        const timePassed = currentTime - lastAttemptTime;
        const timeRemaining = KeycloakAuthService.ATTEMPT_RESET_DURATION - timePassed;
        return Math.max(0, Math.ceil(timeRemaining / 60000)); // Return minutes
    }
    /**
     * Get current login attempts info
     */
    getLoginAttemptsInfo() {
        return {
            currentAttempts: this.getLoginAttempts(),
            maxAttempts: KeycloakAuthService.MAX_LOGIN_ATTEMPTS,
            resetTimeMinutes: this.getLoginAttemptsResetTime(),
            isBlocked: this.isMaxAttemptsExceeded(),
        };
    }
    /**
     * Manually reset login attempts (for administrative purposes)
     */
    forceResetLoginAttempts() {
        this.resetLoginAttempts();
        logger.debug("Login attempts forcefully reset");
    }
}
Object.defineProperty(KeycloakAuthService, "MAX_LOGIN_ATTEMPTS", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: 3
});
Object.defineProperty(KeycloakAuthService, "LOGIN_ATTEMPT_STORAGE_KEY", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: "keycloak_login_attempts"
});
Object.defineProperty(KeycloakAuthService, "LOGIN_ATTEMPT_TIMESTAMP_KEY", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: "keycloak_login_timestamp"
});
Object.defineProperty(KeycloakAuthService, "ATTEMPT_RESET_DURATION", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: 1 * 30 * 1000
}); // 30 seconds
// Export singleton instance for application use
export const keycloakAuthService = new KeycloakAuthService();
